import {
  Button,
  Column,
  Columns,
  FlexBox,
  IconButton,
  Text,
} from '@dixa/lib-shared-habla';
import { AnalyticsMenu, type AnalyticsMenuActions } from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';

import { appActions } from 'app/app.actions';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';

import { analyticsActions } from '../../analytics.actions';
import { AnalyticsControls } from '../../components/controls/analyticsControls';
import { AnalyticsDocumentationButton } from '../../components/documentation/analyticsDocumentationButton';
import { AnalyticsHeader } from '../../components/header/analyticsHeader';
import { AnalyticsPanels } from '../../components/panels/analyticsPanels';
import { AnalyticsQuickViewModal } from '../../components/quickView/components/modal/quickViewModal';

import { useDashboardInit } from './utils/useDashboardInit/useDashboardInit';

export const DashboardView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const mainContentRef = useRef<HTMLDivElement | null>(null);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const { dashboard, isInitialized } = useDashboardInit();
  const { meta, snapshot } = useAppSelector((state) => state.analytics);

  // Sends a tracking event when a dashboard is loaded or changed.
  useEffect(() => {
    if (dashboard && !snapshot) {
      ampli.analyticsDashboardViewed({ name: dashboard.title });

      // Scroll to the top when the dashboard changes.
      if (mainContentRef.current) {
        mainContentRef.current.scrollTop = 0;
      }
    }
  }, [dashboard, snapshot]);

  /* The relative time of the last time the dashboard data was refreshed. */
  const lastRefreshTime = useShortHumanReadableTimer(meta.lastRefresh);

  /* The list of actions available for this view. */
  const actions: AnalyticsMenuActions = [
    {
      type: 'button',
      icon: 'redo',
      label: 'Refresh data',
      description: `Last refreshed ${lastRefreshTime}`,
      disabled: false,
      onClick: () => {
        dispatch(
          analyticsActions.setMeta({
            meta: { lastRefresh: new Date().toISOString() },
          }),
        );

        if (dashboard) {
          ampli.analyticsRefreshClicked({ dashboard: dashboard.title });
        }
      },
    },
  ];

  const handleCreateQuickView = useCallback(() => {
    if (dashboard) {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'analytics:quickView',
            arg: {
              mode: 'create',
              dashboardId: dashboard.id,
              location: 'dashboard',
            },
          },
        }),
      );
    }
  }, [dashboard, dispatch]);

  return (
    <Columns>
      <Column>
        <Column.Row slottable borderBottom>
          <Column.Slot>
            <AnalyticsHeader>
              {dashboard && (
                <>
                  {!isSmallScreen &&
                    dashboard.group &&
                    dashboard.group !== dashboard.title && (
                      <Text.Heading color="tertiary" as="h2">
                        {dashboard.group}
                      </Text.Heading>
                    )}

                  <Text.Heading color="primary" wrap="nowrap" as="h2">
                    {dashboard.title} dashboard
                  </Text.Heading>

                  <AnalyticsDocumentationButton
                    type="dashboard"
                    dashboard={dashboard}
                  />
                </>
              )}
            </AnalyticsHeader>
          </Column.Slot>

          <Column.Slot noVerticalPadding placement="end">
            <FlexBox gap="200">
              {isSmallScreen ? (
                <IconButton
                  icon="bookmark"
                  variant="secondary"
                  onClick={handleCreateQuickView}
                />
              ) : (
                <Button variant="secondary" onClick={handleCreateQuickView}>
                  Save quick view
                </Button>
              )}

              <AnalyticsMenu actions={actions} direction="left" />
            </FlexBox>
          </Column.Slot>
        </Column.Row>

        <Column.Row size={1} scrollable ref={mainContentRef}>
          {isInitialized && (
            <>
              <FlexBox flexDirection="column" gap="400">
                <AnalyticsControls />
                <AnalyticsPanels />
              </FlexBox>

              <Outlet />
            </>
          )}

          <AnalyticsQuickViewModal />
        </Column.Row>
      </Column>
    </Columns>
  );
};
